import moment from "moment";
import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Button, Col, FormControl, Row } from "react-bootstrap";
import { Pen } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { makeRESTApiRequests } from "../../helpers/api";
import {
  ENDPOINTS,
  defaultProposedDateAppChoiceKey,
  defaultScheduledClosingDateDiffAppChoiceKey,
} from "../../helpers/constants";
import Loader from "../Loader";
import useAppChoices from "../../hooks/useAppChoices";
import { getAppChoicesFromKey } from "../../helpers/global";

const localizer = momentLocalizer(moment);

const Event = ({ event }) => {
  return (
    <div className="bg-danger">
      <strong>{event.title}</strong>
      {event.desc && ":  " + event.desc}
    </div>
  );
};

const getTodayMidnight = () => {
  const date = new Date();
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

const MarkHolidays = () => {
  const proposedDateDiffFromAppChoice =
    useAppChoices(defaultProposedDateAppChoiceKey)[0] || 12;
  const scheduledClosingDateDiffFromAppChoice =
    useAppChoices(defaultScheduledClosingDateDiffAppChoiceKey)[0] || 14;

  const [initialHolidays, setInitialHolidays] = useState([]);
  const [editedHolidays, setEditedHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [submittingHolidays, setSubmittingHolidays] = useState(false);
  const [today] = useState(getTodayMidnight());
  const [initialProposedDateDiff, setInitialProposedDateDiff] = useState(
    proposedDateDiffFromAppChoice
  );
  const [
    initialScheduledClosingDateDiff,
    setInitialScheduledClosingDateDiff,
  ] = useState(scheduledClosingDateDiffFromAppChoice);
  const [proposedDateDiff, setProposedDateDiff] = useState();
  const [scheduledClosingDateDiff, setScheduledClosingDateDiff] = useState();

  const getHolidays = async () => {
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.GET_HOLIDAYS,
    });

    if (error) {
      return toast.error(error);
    }

    const availabilityReponse = response;
    setInitialHolidays(
      (availabilityReponse || []).map((h) => {
        const date = new Date(h.year, h.month - 1, h.date);
        return {
          start: date,
          end: date,
          allDay: true,
          title: "Holiday",
          _id: h._id,
        };
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    setEditedHolidays([...initialHolidays]);
  }, [initialHolidays]);

  useEffect(() => {
    setProposedDateDiff(initialProposedDateDiff);
  }, [initialProposedDateDiff]);

  useEffect(() => {
    setScheduledClosingDateDiff(initialScheduledClosingDateDiff);
  }, [initialScheduledClosingDateDiff]);

  useEffect(() => {
    getHolidays();
  }, []);

  const onCancelClick = () => {
    setEditedHolidays([...initialHolidays]);
    setProposedDateDiff(initialProposedDateDiff);
    setScheduledClosingDateDiff(initialScheduledClosingDateDiff);
    setEditMode(false);
  };

  const onSaveClick = async () => {
    toast.info("Please wait updating availabilities...");
    setSubmittingHolidays(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.SET_HOLIDAYS,
      requestBody: {
        holidays: editedHolidays
          .filter((h) => !h._id)
          .map((h) => ({
            year: h.start.getFullYear(),
            month: h.start.getMonth() + 1,
            date: h.start.getDate(),
          })),
        proposedDateDiff,
        scheduledClosingDateDiff,
      },
    });
    setSubmittingHolidays(false);
    if (error) {
      return toast.error(error);
    }

    setInitialHolidays(
      editedHolidays.map((h) => ({
        ...h,
        resource: "",
      }))
    );
    setInitialProposedDateDiff(proposedDateDiff);
    setInitialScheduledClosingDateDiff(scheduledClosingDateDiff);
    setEditMode(false);
    toast.success("Availabilities updated successfully...");

    //update app choices
    const newAppChoices = JSON.parse(localStorage.getItem("app-choices"));

    newAppChoices.find(
      (a) => a.key === defaultProposedDateAppChoiceKey
    ).values = [{ value: proposedDateDiff }];

    newAppChoices.find(
      (a) => a.key === defaultScheduledClosingDateDiffAppChoiceKey
    ).values = [{ value: scheduledClosingDateDiff }];

    localStorage.setItem("app-choices", JSON.stringify(newAppChoices));
  };

  const onDateSelect = (e) => {
    const { start: startDateString, end: endDateString } = e;
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);

    while (startDate.getTime() < endDate.getTime()) {
      //check if already contains
      if (
        startDate.getTime() < today.getTime() ||
        startDate.getDay() === 6 ||
        startDate.getDay() === 0
      ) {
        startDate.setDate(startDate.getDate() + 1);
        continue;
      }

      const holidayIndex = editedHolidays.findIndex((h) => {
        return (
          h.start.getDate() === startDate.getDate() &&
          h.start.getMonth() === startDate.getMonth() &&
          h.start.getFullYear() === startDate.getFullYear()
        );
      });

      if (holidayIndex !== -1) {
        //contains, so remove
        editedHolidays.splice(holidayIndex, 1);
      } else {
        //does not contain, add
        const newDate = new Date(startDate);
        editedHolidays.push({
          start: newDate,
          end: newDate,
          allDay: true,
          title: "Holiday",
          resource: "editing",
        });
      }

      startDate.setDate(startDate.getDate() + 1);
    }

    setEditedHolidays([...editedHolidays]);
  };

  const customDayPropGetter = (date) => {
    if (
      date.getTime() < today.getTime() ||
      date.getDay() === 6 ||
      date.getDay() === 0
    )
      return {
        style: {
          background: "#F5F5F5",
        },
      };
    else return {};
  };

  return (
    <div className="p-3">
      {loading ? (
        <Loader />
      ) : (
        <>
          <Row>
            <Col className="align-self-center" xs={12} md={6}>
              <h6 className="mb-0 small-text">
                Date differences for a new job:
              </h6>
            </Col>
            <Col className="text-right align-self-center" xs={12} md={6}>
              {editMode ? (
                <>
                  <Button
                    onClick={onCancelClick}
                    size="sm"
                    variant="secondary"
                    className="mr-2"
                    disabled={submittingHolidays}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={submittingHolidays}
                    onClick={onSaveClick}
                    size="sm"
                    variant="primary"
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setEditMode(true)}
                  disabled={submittingHolidays}
                >
                  <Pen size={14} className="mr-2" /> Edit
                </Button>
              )}
            </Col>
          </Row>
          <hr className="my-1" />
          <Row>
            <Col xs={12} md={6}>
              <h6 className="tiny-text mb-1 text-muted">Proposed Due Date</h6>
              <FormControl
                size="sm"
                value={proposedDateDiff}
                onChange={(e) => setProposedDateDiff(e.target.value)}
                disabled={!editMode}
                type="number"
              />
            </Col>
            <Col xs={12} md={6}>
              <h6 className="tiny-text mb-1 text-muted">
                Scheduled Closing Date
              </h6>
              <FormControl
                size="sm"
                type="number"
                value={scheduledClosingDateDiff}
                onChange={(e) => setScheduledClosingDateDiff(e.target.value)}
                disabled={!editMode}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col className="align-self-center" xs={12}>
              <h6 className="mb-0  small-text">Holidays Marker</h6>
            </Col>
          </Row>
          <hr className="my-1" />
          <Calendar
            startAccessor="start"
            endAccessor="end"
            localizer={localizer}
            style={styles.calendar}
            events={editedHolidays}
            view="month"
            views={["month"]}
            eventPropGetter={(e) => ({
              className: e.resource ? "bg-secondary" : "bg-danger",
            })}
            selectable={editMode}
            onSelectSlot={onDateSelect}
            dayPropGetter={customDayPropGetter}
          />
        </>
      )}
    </div>
  );
};

const styles = {
  calendar: {
    height: "500px",
  },
};
export default MarkHolidays;
